import { Util } from '@h3/h3common';

const MAX_RETRY_COUNT = 1;

const localStorageKey = 'H3_ConnectionId';

interface IRequest {
    (connectionId: string, signal: AbortSignal): Promise<Response>;
}

interface IHooks {
    onRead?: (data: string) => void;
    onConnect?: (connectionId: string) => void;
    onDisconnect?: (isException: boolean, e?: unknown) => void;
}

class StreamService {
    private _connectionId: string;

    H3_CONNECTION_ID: string;

    constructor() {
        this._connectionId = this._getConnectionId();
        this.H3_CONNECTION_ID = 'H3-ConnectionId';
    }

    connect(request: IRequest, hooks?: IHooks) {
        const abortController = new AbortController();
        this._connect(request, abortController, hooks);
        return () => abortController.abort();
    }

    private async _connect(request: IRequest, abortController: AbortController, hooks?: IHooks, retry = 0) {
        request(this._connectionId, abortController.signal)
            .then(async (response) => {
                const stream = response.body;
                if (!stream) {
                    throw new Error();
                }
                hooks?.onConnect?.(this._connectionId);
                const reader = stream.getReader();
                const decoder = new TextDecoder();
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) {
                        hooks?.onDisconnect?.(false);
                        break;
                    }
                    const chunk = decoder.decode(value);
                    hooks?.onRead?.(chunk);
                }
            })
            .catch((e) => {
                // cancel or page refresh
                if (abortController.signal.aborted || Util.Dom.isAbortError(e)) {
                    hooks?.onDisconnect?.(false);
                    return;
                }
                // retry
                if (retry < MAX_RETRY_COUNT) {
                    // reset connectionId
                    this._connectionId = Util.Random.getRandomUUID();
                    this._cacheConnectionId(this._connectionId);
                    this._connect(request, abortController, hooks, retry + 1);
                    return;
                }
                // exception
                hooks?.onDisconnect?.(true, e);
            });
    }

    private _cacheConnectionId(id: string) {
        Util.Storage.Storage.set(localStorageKey, id, null);
    }

    private _getConnectionId() {
        const source = Util.Storage.Storage.get<string>(localStorageKey);
        if (source) {
            return source;
        }
        const id = Util.Random.getRandomUUID();
        this._cacheConnectionId(id);
        return id;
    }
}

export default new StreamService();
